/**
 * Settings Module - Simplified v5.1.1
 * Cleaned up version with essential features only
 */

import AppState from '../../core/AppState.js';
import EventBus, { AppEvents } from '../../core/EventBus.js';
import Config from '../../core/Config.js';
import InitiativeService from '../../services/InitiativeService.js';
import ApiService from '../../services/ApiService.js';
import StorageService from '../../services/StorageService.js';
import { bindPromptEditors } from '../../components/PromptBinder.js';
import * as ActionRegistry from '../../core/ActionRegistry.js';
import { ModalService } from '../../ui-kit/ModalService.jsx';
import { icon as devIcon } from '../../ui/icons.js';

export class SettingsModule {
  constructor() {
    this.container = null;
    this.settings = this.loadSettings();
    this.showApiKey = false;
    this.eventHandlers = new Map();
    this.initialized = false;
  }

  async initialize() {
    console.log('🔧 Settings module initialized');
    this.initialized = true;
  }

  mount(container) {
    this.container = container;
    this._fbPage = 1;
    this._fbPageSize = 10;
    this._fbTotal = 0;
    this.render();
    this.attachEventListeners();
    this.registerRouterActions();
    // Bind prompt edit buttons within Settings scope
    try { bindPromptEditors(this.container); } catch (e) { console.warn('PromptBinder error (Settings):', e); }
    // Mount BuildInfo Preact island into System Info card
    try {
      const host = this.container.querySelector('.settings-card .system-info');
      if (host) {
        Promise.all([
          import('../../ui/settings/BuildInfo.jsx'),
          import('preact')
        ]).then(([mod, preact]) => {
          const BuildInfo = mod.BuildInfo || mod.default;
          if (BuildInfo && preact.render) preact.render(preact.h(BuildInfo, {}), host);
        }).catch(()=>{});
      }
    } catch (_) {}

    // Mount API Settings island
    try {
      const apiHost = this.container.querySelector('#api-settings-island');
      if (apiHost) {
        Promise.all([
          import('../../ui/settings/APISettings.jsx'),
          import('preact')
        ]).then(([mod, preact]) => {
          const APISettings = mod.APISettings || mod.default;
          if (APISettings && preact.render) preact.render(preact.h(APISettings, {}), apiHost);
        }).catch(()=>{});
      }
    } catch(_) {}

    // Mount Prompt List island
    try {
      const listHost = this.container.querySelector('#prompt-list-island');
      if (listHost) {
        Promise.all([
          import('../../ui/settings/PromptList.jsx'),
          import('preact')
        ]).then(([mod, preact]) => {
          const PromptList = mod.PromptList || mod.default;
          if (PromptList && preact.render) preact.render(preact.h(PromptList, {}), listHost);
        }).catch(()=>{});
      }
    } catch (_) {}
    console.log('⚙️ Settings module mounted');
    // Only attempt to render analytics widgets if the section exists (not when moved to Insights)
    if (this.container.querySelector('#analytics-charts')) {
      try { this.updateAnalyticsSummary(); } catch (_) {}
      try { this.updateAnalyticsCharts(); } catch (_) {}
      try { this.updateFeedbackTable(); } catch (_) {}
    }
  }

  /**
   * Load settings from storage
   */
  loadSettings() {
    return {
      apiKey: this.getApiKey(),
      model: localStorage.getItem('ai_model') || 'gpt-4o',
      temperature: parseFloat(localStorage.getItem('ai_temperature') || '0.7'),
      debugMode: localStorage.getItem('debug_mode') === 'true'
    };
  }

  /**
   * Get API key from multiple sources
   */
  getApiKey() {
    // Try multiple sources in order of preference
    return localStorage.getItem('openai_api_key') || 
           localStorage.getItem('sip_apiKey')?.replace(/['"]/g, '') || 
           window.ApiService?.apiKey || 
           '';
  }

  /**
   * Render the settings interface
   */
  render() {
    if (!this.container) return;
    const isDev = !!import.meta.env?.DEV;

    this.container.innerHTML = `
      <div class="settings-module">
        <div class="settings-header"></div>

        <div class="settings-grid-layout-simplified">
          <!-- Left Column: Core Settings -->
          <div class="settings-column">
            <!-- API Configuration -->
            <div class="settings-section panel" data-theme="unified">
              <header class="panel__header"><h3 class="panel__title">API Configuration</h3></header>
              <div class="panel__content settings-card">
                <form id="api-settings-form">
                  <div id="api-settings-island"></div>
                </form>
              </div>
            </div>

            <!-- Prompt Management -->
            <div class="settings-section panel" data-theme="unified">
              <header class="panel__header"><h3 class="panel__title">Prompt Management</h3></header>
              <div class="panel__content settings-card">
                <div id="prompt-list-island"></div>
                <div class="form-actions-group" style="margin-top:12px">
                  <button class="btn btn-primary" data-action="prompts-export">Export Prompts (JSON)</button>
                  <button class="btn btn-ghost" data-action="prompts-import">Import Prompts</button>
                  <input type="file" id="prompts-import-file" accept="application/json" style="display:none" />
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: System Info & Maintenance -->
          <div class="settings-column">
            <!-- System Information -->
            <div class="settings-section panel" data-theme="unified">
              <header class="panel__header"><h3 class="panel__title">System Information</h3></header>
              <div class="panel__content settings-card">
                <div class="system-info">
                  <div class="info-row">
                    <span class="info-label">Version:</span>
                    <span class="info-value">${Config.APP_VERSION}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Environment:</span>
                    <span class="info-value">${Config.APP_ENV}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Initiatives:</span>
                    <span class="info-value" id="initiatives-count">${this.getInitiativeCount()}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Storage Used:</span>
                    <span class="info-value" id="storage-size">${this.getStorageSize()}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">API Key Status:</span>
                    <span class="info-value" id="api-status">${this.settings.apiKey ? '✅ Configured' : '❌ Not Set'}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Custom Prompts:</span>
                    <span class="info-value" id="custom-prompts-count">${this.getCustomPromptsCount()}</span>
                  </div>
                </div>

                <div class="form-group" style="margin-top: 1rem;">
                  <label class="checkbox-label">
                    <input 
                      type="checkbox" 
                      id="debug-mode-checkbox"
                      ${this.settings.debugMode ? 'checked' : ''}
                    >
                    <span>Enable Debug Mode</span>
                  </label>
                  <small class="form-hint">Shows additional logging in browser console</small>
                </div>

                <div style="margin-top: 1rem; display:flex; gap: 8px; align-items:center;">
                  <button class="btn btn-small" data-action="settings-show-diagnostics">Router Diagnostics</button>
                  <small class="text-secondary">Dev-only: logs last routed actions</small>
                </div>
              </div>
            </div>

            <!-- Feedback & Analytics (moved to Insights when enabled) -->
            <div class="settings-section panel" data-theme="unified">
              ${Config.FEATURES?.INSIGHTS_TAB === true ? `
                <header class="panel__header"><h3 class="panel__title">Feedback & Analytics</h3></header>
                <div class="panel__content settings-card">
                  <p style="margin:0 0 8px">Feedback and analytics are available under the <strong>Insights</strong> tab.</p>
                  <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
                    <label class="checkbox-label" style="display:inline-flex;align-items:center;gap:6px">
                      <input type="checkbox" id="analytics-toggle" ${((import.meta.env?.VITE_ENABLE_ANALYTICS === 'true') && (localStorage.getItem('enableAnalytics') === 'true')) ? 'checked' : ''} ${import.meta.env?.VITE_ENABLE_ANALYTICS === 'true' ? '' : 'disabled'}>
                      <span>Enable local analytics and feedback (opt-in)</span>
                    </label>
                    <button class="btn btn-small" data-action="go-insights">Open Insights</button>
                  </div>
                  ${import.meta.env?.VITE_ENABLE_ANALYTICS === 'true' ? '' : '<small class="form-hint">Disabled by environment flag</small>'}
                </div>
              ` : `
                <header class="panel__header"><h3 class="panel__title">Feedback & Analytics</h3></header>
                <div class="panel__content settings-card">
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input type="checkbox" id="analytics-toggle" ${((import.meta.env?.VITE_ENABLE_ANALYTICS === 'true') && (localStorage.getItem('enableAnalytics') === 'true')) ? 'checked' : ''} ${import.meta.env?.VITE_ENABLE_ANALYTICS === 'true' ? '' : 'disabled'}>
                      <span>Enable local analytics and feedback (opt-in)</span>
                    </label>
                    ${import.meta.env?.VITE_ENABLE_ANALYTICS === 'true' ? '' : '<small class="form-hint">Disabled by environment flag</small>'}
                  </div>
                  <div class="analytics-summary" style="display:flex;gap:12px;flex-wrap:wrap;margin-top:8px">
                    <div class="stat-card" style="padding:8px 12px;background:var(--bg-secondary);border-radius:6px"><strong id="anx-events">0</strong> events</div>
                    <div class="stat-card" style="padding:8px 12px;background:var(--bg-secondary);border-radius:6px"><strong id="anx-msg-feedback">0</strong> message feedback</div>
                    <div class="stat-card" style="padding:8px 12px;background:var(--bg-secondary);border-radius:6px"><strong id="anx-app-feedback">0</strong> app feedback</div>
                  </div>
                  <div id="analytics-charts" style="margin-top:12px;display:grid;grid-template-columns:1fr 1fr;gap:16px">
                    <div>
                      <h4 style="margin:0 0 8px">Time by Tab</h4>
                      <div id="chart-tabtime"></div>
                    </div>
                    <div>
                      <h4 style="margin:0 0 8px">Top Actions</h4>
                      <div id="chart-actions"></div>
                    </div>
                  </div>
                  <div class="form-actions-group" style="margin-top:12px">
                    <button class="btn-primary" data-action="analytics-export">Export JSON</button>
                    <button class="btn btn-ghost" data-action="analytics-clear">Clear Telemetry</button>
                    <button class="btn btn-ghost" data-action="export-jsonl-msg">Export Message Feedback (JSONL)</button>
                    <label class="checkbox-label" style="display:flex;align-items:center;gap:6px"><input type="checkbox" id="jsonl-include-text"> Include text</label>
                  </div>
                  <div id="feedback-review" style="margin-top:16px">
                    <h4 style="margin:0 0 8px">Review Feedback</h4>
                    <div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:8px">
                      <label>Type
                        <select id="fb-type" class="form-control">
                          <option value="message">Message</option>
                          <option value="app">App</option>
                        </select>
                      </label>
                      <label id="fb-vote-wrap">Vote
                        <select id="fb-vote" class="form-control">
                          <option value="all">All</option>
                          <option value="up">👍 Up</option>
                          <option value="down">👎 Down</option>
                          <option value="none">None</option>
                        </select>
                      </label>
                      <label id="fb-rating-wrap">Min Rating
                        <select id="fb-rating" class="form-control">
                          <option value="0">Any</option>
                          <option value="1">1+</option>
                          <option value="2">2+</option>
                          <option value="3">3+</option>
                          <option value="4">4+</option>
                          <option value="5">5</option>
                        </select>
                      </label>
                      <label id="fb-category-wrap" style="display:none">Category
                        <select id="fb-category" class="form-control">
                          <option value="all">All</option>
                          <option value="ux">UX</option>
                          <option value="bug">Bug</option>
                          <option value="idea">Idea</option>
                          <option value="other">Other</option>
                        </select>
                      </label>
                    </div>
                    <div id="fb-table"></div>
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px">
                      <button class="btn btn-ghost" id="fb-prev">Prev</button>
                      <span id="fb-page-info">Page 1</span>
                      <button class="btn btn-ghost" id="fb-next">Next</button>
                    </div>
                  </div>
                </div>
              `}
            </div>

            <!-- Maintenance -->
            <div class="settings-section panel" data-theme="unified">
              <header class="panel__header"><h3 class="panel__title">Maintenance</h3></header>
              <div class="panel__content settings-card">
                <div class="maintenance-actions">
                  <div class="action-item">
                    <button class="btn btn-ghost full-width" id="clear-cache-btn" data-action="settings-clear-cache">
                      Clear Cache
                    </button>
                    <small class="action-description">
                      Removes: Temporary data, API cache, session data<br>
                      Keeps: All initiatives, settings, and custom prompts
                    </small>
                  </div>
                  
                  <div class="action-item">
                    <button class="btn btn-ghost full-width" id="reset-settings-btn" data-action="settings-reset-settings">
                      Reset Settings
                    </button>
                    <small class="action-description">
                      Resets: AI model, temperature, debug mode<br>
                      Keeps: API key, initiatives, custom prompts
                    </small>
                  </div>

                  <div class="action-item">
                    <button class="btn btn-ghost full-width" id="cleanup-legacy-btn" data-action="settings-cleanup-legacy">
                      Cleanup Legacy Storage Keys
                    </button>
                    <small class="action-description">
                      Removes old localStorage keys (initiatives/goalsDatabase/customPrompts). Safe to run.
                    </small>
                  </div>

                  <div class="action-item">
                    <button class="btn btn-danger full-width" id="delete-initiatives-btn" data-action="settings-delete-initiatives">
                      Delete Initiatives Database
                    </button>
                    <small class="action-description danger-description">
                      Deletes all saved initiatives from the local database.<br>
                      Keeps: API key, settings, custom prompts, strategies.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Danger Zone -->
            <div class="settings-section danger-zone panel" data-theme="unified">
              <header class="panel__header"><h3 class="panel__title">Danger Zone</h3></header>
              <div class="panel__content settings-card">
                <div class="danger-warning">
                  <p><strong>Warning:</strong> This action cannot be undone!</p>
                </div>
                <div class="action-item">
                  <button class="btn btn-danger full-width" id="delete-all-btn" data-action="settings-delete-all">
                    Delete All Data
                  </button>
                  <small class="action-description danger-description">
                    <strong>Permanently deletes EVERYTHING:</strong><br>
                    • All initiatives and objectives<br>
                    • All custom prompts and settings<br>
                    • API key configuration<br>
                    • All strategies and analysis<br>
                    • Entire application data
                  </small>
                </div>
              </div>
            </div>

            <!-- About -->
            <div class="settings-section panel" data-theme="unified">
              <header class="panel__header"><h3 class="panel__title">About</h3></header>
              <div class="panel__content settings-card">
              <div class="about-content">
                <h4>Strategic Intelligence Platform</h4>
                <p>AI-powered strategic planning and goal management system.</p>
                <p style="margin-top: 1rem;">
                  <strong>Version ${Config.APP_VERSION}</strong><br>
                  <small>Modern Architecture Edition</small>
                </p>
                <div class="copyright">
                  <small>© 2025 Rami Sirkia. All rights reserved.</small>
                </div>
                <div style="margin-top: 8px;">
                  <a href="/third-party-notices.html" target="_blank" rel="noreferrer noopener">Third‑Party Notices</a>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Attach all event listeners
   */
  attachEventListeners() {
    if (!this.container) return;

    // Clear existing handlers
    this.removeEventListeners();

    // Clicks are routed via ActionRouter; keep non-click handlers below

    // Temperature slider real-time update
    this.addEventHandler('input', '#temperature-slider', (e) => {
      document.getElementById('temp-display').textContent = e.target.value;
    });

    // Form submission
    this.addEventHandler('submit', '#api-settings-form', (e) => this.handleApiSettingsSubmit(e));

    // Debug mode toggle
    this.addEventHandler('change', '#debug-mode-checkbox', (e) => this.handleDebugModeToggle(e));

    // Analytics toggle
    this.addEventHandler('change', '#analytics-toggle', (e) => this.handleAnalyticsToggle(e));

    // Non-click event handlers remain (input/submit/change)
  }

  registerRouterActions() {
    const mapping = {
      'go-insights': async () => {
        try { AppState.setState({ currentTab: 'insights' }); } catch(_) {}
      },
      'settings-clear-cache': ({}) => this.clearCache(),
      'settings-cleanup-legacy': ({}) => this.cleanupLegacy(),
      'settings-reset-settings': ({}) => this.resetSettings(),
      'settings-delete-initiatives': { handler: () => this.deleteInitiatives(), destructive: true },
      'settings-delete-all': { handler: () => this.deleteAllData(), destructive: true, throttleMs: 500 },
      'settings-show-diagnostics': ({}) => this.showDiagnostics(),
      'prompts-export': async ({}) => {
        try {
          const { default: PromptService } = await import('../../services/PromptService.js');
          const { default: StorageService } = await import('../../services/StorageService.js');
          const svc = PromptService;
          const base = svc.prompts || {};
          const preferredOrder = [
            'outcomeAssistant',
            'portfolioAnalysis',
            'riskAnalysis',
            'strategyFileUpload',
            'emergentStrategy',
            'initiativeExtractor',
            'initiativeSearch',
            'transferStrategy',
            'initiativeTransfer'
          ];
          const resolveSystemRole = (key, p) => {
            try { return (svc.getCurrentVersion?.(key)?.systemRole) || p.systemRole || ''; } catch(_) { return p.systemRole || ''; }
          };
          const out = {};
          // 1) Include all preferred prompts from in‑memory service
          preferredOrder.forEach((key) => {
            const p = base[key];
            if (!p) return;
            out[key] = {
              name: p.name || key,
              description: p.description || '',
              systemRole: resolveSystemRole(key, p),
              temperature: typeof p.temperature === 'number' ? p.temperature : 0.7,
              promptVersion: p.currentVersion || '1.0.0',
              customized: !!p.customized
            };
          });
          // 2) Merge any customized extras present in storage
          const sip = StorageService.get('customPrompts', {});
          const legacy = (() => { try { return JSON.parse(localStorage.getItem('customPrompts') || '{}'); } catch { return {}; } })();
          const mergeMap = (map) => {
            Object.keys(map || {}).forEach((k) => {
              const p = map[k] || {};
              if (!out[k]) out[k] = {};
              out[k] = {
                name: out[k].name || (base[k]?.name) || k,
                description: out[k].description || (base[k]?.description) || '',
                systemRole: String(p.systemRole || out[k].systemRole || resolveSystemRole(k, base[k] || {})),
                temperature: typeof p.temperature === 'number' ? p.temperature : (typeof out[k].temperature === 'number' ? out[k].temperature : 0.7),
                promptVersion: out[k].promptVersion || (base[k]?.currentVersion) || '1.0.0',
                customized: true
              };
            });
          };
          mergeMap(sip);
          mergeMap(legacy);
          // 3) Add any other prompts from service not in preferred list
          Object.keys(base).forEach((k) => {
            if (out[k]) return;
            const p = base[k];
            out[k] = {
              name: p.name || k,
              description: p.description || '',
              systemRole: resolveSystemRole(k, p),
              temperature: typeof p.temperature === 'number' ? p.temperature : 0.7,
              promptVersion: p.currentVersion || '1.0.0',
              customized: !!p.customized
            };
          });
          const payload = { prompts: out, exportedAt: new Date().toISOString(), total: Object.keys(out).length };
          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `prompts-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { console.warn('Export prompts failed', e); this.showToast('Export failed', 'error'); }
      },
      'prompts-import': async ({}) => {
        try {
          const input = this.container.querySelector('#prompts-import-file');
          input.value = '';
          input.onchange = async () => {
            const file = input.files && input.files[0];
            if (!file) return;
            try {
              const text = await file.text();
              const json = JSON.parse(text);
              const map = json.prompts || json || {};
              // Validate minimal shape and persist
              const cleaned = {};
              Object.keys(map).forEach(k => {
                const p = map[k] || {};
                const t = typeof p.temperature === 'number' ? p.temperature : parseFloat(p.temperature || '0.7');
                cleaned[k] = { systemRole: String(p.systemRole || ''), temperature: isNaN(t) ? 0.7 : t, customized: true, lastModified: new Date().toISOString() };
              });
              const { default: StorageService } = await import('../../services/StorageService.js');
              const { default: PromptService } = await import('../../services/PromptService.js');
              StorageService.set('customPrompts', cleaned);
              // Also store under service blob key for compatibility
              StorageService.set('promptServiceData', { prompts: cleaned, version: 'import', lastSaved: new Date().toISOString() });
              // Reload prompts into service
              try { PromptService.loadCustomPrompts(); } catch(_) {}
              this.showToast('Prompts imported', 'success');
            } catch (err) {
              console.error('Import prompts failed:', err);
              this.showToast('Import failed: invalid file', 'error');
            }
          };
          input.click();
        } catch (e) { console.warn('Import prompts failed', e); }
      },
      'analytics-export': async ({}) => {
        try {
          const mod = await import('../../services/AnalyticsService.js');
          const data = await mod.default.exportAll();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `sip-analytics-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { console.warn('Export failed', e); }
      },
      'analytics-clear': async ({}) => {
        try { (await import('../../services/AnalyticsService.js')).default.clearTelemetry(); } catch (_) {}
        try { (await import('../../services/FeedbackService.js')).default.clearAllFeedback(); } catch (_) {}
        this.showToast('Telemetry cleared', 'success');
        setTimeout(() => this.updateAnalyticsSummary(), 200);
      },
      'export-jsonl-msg': async ({}) => {
        try {
          const includeText = !!document.getElementById('jsonl-include-text')?.checked;
          const svc = (await import('../../services/AnalyticsService.js')).default;
          const data = await svc.exportMessageFeedbackJSONL({ includeText });
          const blob = new Blob([data], { type: 'application/jsonl' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `message-feedback-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.jsonl`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { console.warn('Export JSONL failed', e); }
      }
    };
    ActionRegistry.registerMap('settings', mapping);
  }

  showDiagnostics() {
    try {
      const router = (typeof window !== 'undefined' && window.__ACTION_ROUTER_REF__) || null;
      const logs = (router && router._actionLog ? router._actionLog : []).slice(-10).reverse();
      const flags = [
        `Router installed: ${window.__ROUTER_INSTALLED__ ? 'yes' : 'no'}`,
        `ACTION_LOG: ${window.ACTION_LOG === true ? 'on' : 'off'}`,
        `Non-blocking: ${router && router.nonBlocking ? (router.nonBlocking() ? 'yes' : 'no') : 'unknown'}`
      ];
      const items = logs.map(l => `• [${l.time}] (${l.ns}) ${l.action} — ${l.status}${l.error ? ' — ' + l.error : ''}`).join('<br>') || '<em>No actions logged yet</em>';
      // Scan for buttons without data-action (orphaned)
      const orphans = Array.from(document.querySelectorAll('button, [role="button"], .btn'))
        .filter(el => !el.closest('.uk-modal'))
        .filter(el => !el.dataset.action)
        .slice(0, 10)
        .map(el => el.outerHTML.replace(/</g,'&lt;').replace(/>/g,'&gt;'))
        .join('<br>') || '<em>No obvious orphaned buttons</em>';
      const content = `
        <div style="line-height:1.5">
          <h4>Flags</h4>
          <div style="font-family:monospace">${flags.join('<br>')}</div>
          <h4 style="margin-top:12px">Last 10 Actions</h4>
          <div style="font-family:monospace">${items}</div>
          <h4 style="margin-top:12px">Orphaned Buttons (first 10)</h4>
          <div style="font-family:monospace;font-size:12px;max-height:200px;overflow:auto;background:var(--bg-secondary);padding:8px;border-radius:6px">${orphans}</div>
        </div>`;
      ModalService.open({ title: '🧪 Router Diagnostics', content, actions: [{ text: 'Close', class: 'uk-btn uk-btn-secondary', action: () => {} }] });
    } catch (e) { console.warn('Diagnostics modal failed', e); }
  }

  /**
   * Event handler management
   */
  addEventHandler(event, selector, handler) {
    const element = this.container.querySelector(selector);
    if (element) {
      element.addEventListener(event, handler);
      this.eventHandlers.set(selector + ':' + event, { element, event, handler });
    }
  }

  removeEventListeners() {
    this.eventHandlers.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventHandlers.clear();
  }

  /**
   * Toggle API key visibility
   */
  toggleApiKeyVisibility() {
    this.showApiKey = !this.showApiKey;
    const input = this.container.querySelector('#api-key-input');
    const button = this.container.querySelector('#toggle-api-key');
    
    if (input && button) {
      input.type = this.showApiKey ? 'text' : 'password';
      button.innerHTML = this.showApiKey ? '🙈' : '👁️';
      button.title = this.showApiKey ? 'Hide API key' : 'Show API key';
    }
  }

  handleAnalyticsToggle(e) {
    try {
      const on = !!e.target.checked;
      localStorage.setItem('enableAnalytics', on ? 'true' : 'false');
      this.showToast(on ? 'Analytics enabled (local only)' : 'Analytics disabled', 'info');
      setTimeout(async () => {
        this.updateAnalyticsSummary();
        if (on) {
          try { (await import('../../services/FeedbackService.js')).default.flushQueue(); } catch (_) {}
        }
      }, 300);
    } catch (error) {
      console.error('Failed to toggle analytics:', error);
    }
  }

  async updateAnalyticsSummary() {
    try {
      const mod = await import('../../services/AnalyticsService.js');
      const data = await mod.default.exportAll();
      const byId = (id, val) => { const el = this.container?.querySelector('#' + id); if (el) el.textContent = String(val); };
      byId('anx-events', data.events?.length || 0);
      byId('anx-msg-feedback', data.message_feedback?.length || 0);
      byId('anx-app-feedback', data.app_feedback?.length || 0);
    } catch (_) {}
  }

  async updateAnalyticsCharts() {
    try {
      const svc = (await import('../../services/AnalyticsService.js')).default;
      const tabs = await svc.getTabDurations();
      const actions = await svc.getTopActions(5);
      const tabRoot = this.container.querySelector('#chart-tabtime');
      const actRoot = this.container.querySelector('#chart-actions');
      const bar = (label, value, max) => `
        <div style="display:flex;align-items:center;gap:8px;margin:4px 0">
          <div style="width:120px;color:var(--text-secondary)">${label}</div>
          <div style="flex:1;background:var(--bg-secondary);border-radius:4px;overflow:hidden">
            <div style="width:${max ? Math.max(2, Math.round((value/max)*100)) : 0}%;background:#005EB8;color:#fff;padding:2px 6px;font-size:12px">${this.formatDuration(value)}</div>
          </div>
        </div>`;
      const countBar = (label, count, max) => `
        <div style="display:flex;align-items:center;gap:8px;margin:4px 0">
          <div style="width:160px;color:var(--text-secondary)">${label}</div>
          <div style="flex:1;background:var(--bg-secondary);border-radius:4px;overflow:hidden">
            <div style="width:${max ? Math.max(2, Math.round((count/max)*100)) : 0}%;background:#6B8E71;color:#fff;padding:2px 6px;font-size:12px">${count}</div>
          </div>
        </div>`;
      const maxTab = Math.max(1, ...tabs.map(t => t.totalMs));
      tabRoot.innerHTML = tabs.slice(0,6).map(t => bar(t.tabId, t.totalMs, maxTab)).join('') || '<em>No data</em>';
      const maxAct = Math.max(1, ...actions.map(a => a.count));
      actRoot.innerHTML = actions.map(a => countBar(a.actionKey, a.count, maxAct)).join('') || '<em>No data</em>';
    } catch (_) {}
  }

  formatDuration(ms) {
    const s = Math.round(ms/1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s/60);
    const rs = s % 60;
    return `${m}m ${rs}s`;
  }

  onFeedbackTypeChange() {
    const type = this.container.querySelector('#fb-type')?.value || 'message';
    const voteWrap = this.container.querySelector('#fb-vote-wrap');
    const ratingWrap = this.container.querySelector('#fb-rating-wrap');
    const catWrap = this.container.querySelector('#fb-category-wrap');
    const isMsg = type === 'message';
    voteWrap.style.display = isMsg ? '' : 'none';
    ratingWrap.style.display = isMsg ? '' : 'none';
    catWrap.style.display = isMsg ? 'none' : '';
    this._fbPage = 1;
    this.updateFeedbackTable();
  }

  paginateFeedback(delta) {
    const pages = Math.max(1, Math.ceil(this._fbTotal / this._fbPageSize));
    this._fbPage = Math.min(pages, Math.max(1, this._fbPage + delta));
    this.updateFeedbackTable();
  }

  async updateFeedbackTable() {
    try {
      const type = this.container.querySelector('#fb-type')?.value || 'message';
      const table = this.container.querySelector('#fb-table');
      const pageInfo = this.container.querySelector('#fb-page-info');
      const offset = (this._fbPage - 1) * this._fbPageSize;
      let rows = [], total = 0;
      if (type === 'message') {
        const vote = this.container.querySelector('#fb-vote')?.value || 'all';
        const minRating = parseInt(this.container.querySelector('#fb-rating')?.value || '0', 10);
        const svc = (await import('../../services/FeedbackService.js')).default;
        const res = await svc.queryMessageFeedback({ limit: this._fbPageSize, offset, vote, minRating });
        rows = res.rows; total = res.total;
        table.innerHTML = this.renderMsgFeedbackTable(rows);
      } else {
        const category = this.container.querySelector('#fb-category')?.value || 'all';
        const svc = (await import('../../services/FeedbackService.js')).default;
        const res = await svc.queryAppFeedback({ limit: this._fbPageSize, offset, category });
        rows = res.rows; total = res.total;
        table.innerHTML = this.renderAppFeedbackTable(rows);
      }
      this._fbTotal = total;
      const pages = Math.max(1, Math.ceil(total / this._fbPageSize));
      pageInfo.textContent = `Page ${this._fbPage} / ${pages} (${total} total)`;
    } catch (e) { console.warn('Feedback table update failed', e); }
  }

  renderMsgFeedbackTable(rows) {
    if (!rows.length) return '<em>No feedback yet</em>';
    const tr = (r) => `<tr>
      <td>${new Date(r.ts).toLocaleString()}</td>
      <td>${r.tabId || ''}</td>
      <td>${r.promptKey || ''}</td>
      <td>${r.vote || 'none'}</td>
      <td>${r.rating ?? ''}</td>
      <td>${(r.comment || '').replace(/</g,'&lt;')}</td>
    </tr>`;
    return `<table class="data-table" style="width:100%;border-collapse:collapse"><thead>
      <tr><th>Time</th><th>Tab</th><th>Prompt</th><th>Vote</th><th>Rating</th><th>Comment</th></tr>
    </thead><tbody>${rows.map(tr).join('')}</tbody></table>`;
  }

  renderAppFeedbackTable(rows) {
    if (!rows.length) return '<em>No feedback yet</em>';
    const tr = (r) => `<tr>
      <td>${new Date(r.ts).toLocaleString()}</td>
      <td>${r.category || ''}</td>
      <td>${(r.text || '').replace(/</g,'&lt;')}</td>
      <td>${(r.contact || '').replace(/</g,'&lt;')}</td>
    </tr>`;
    return `<table class="data-table" style="width:100%;border-collapse:collapse"><thead>
      <tr><th>Time</th><th>Category</th><th>Text</th><th>Contact</th></tr>
    </thead><tbody>${rows.map(tr).join('')}</tbody></table>`;
  }

  /**
   * Handle API settings form submission
   */
  handleApiSettingsSubmit(e) {
    e.preventDefault();
    
    const apiKey = this.container.querySelector('#api-key-input').value.trim();
    const model = this.container.querySelector('#ai-model-select').value;
    const temperature = parseFloat(this.container.querySelector('#temperature-slider').value);

    // Validate API key
    if (apiKey && !apiKey.startsWith('sk-')) {
      this.showToast('Invalid API key format. Must start with "sk-"', 'error');
      return;
    }

    try {
      // Save to all storage locations for compatibility
      if (apiKey) {
        localStorage.setItem('openai_api_key', apiKey);
        localStorage.setItem('sip_apiKey', JSON.stringify(apiKey));
        try { ApiService.setApiKey(apiKey); } catch (_) {}
      }

      localStorage.setItem('ai_model', model);
      localStorage.setItem('ai_temperature', temperature.toString());

      // Update internal state
      this.settings.apiKey = apiKey;
      this.settings.model = model;
      this.settings.temperature = temperature;

      this.showToast('API settings saved successfully!', 'success');
      this.updateSystemInfo();
      
      // Emit event for other modules
      EventBus.emit(AppEvents.SETTINGS_UPDATED, { 
        api: true, 
        apiKey: !!apiKey,
        model,
        temperature 
      });

    } catch (error) {
      console.error('Failed to save API settings:', error);
      this.showToast('Failed to save API settings', 'error');
    }
  }

  async clearCache() {
    try {
      // Clear known caches and temp items
      sessionStorage.clear();
      if (window.ApiService && window.ApiService.cache) {
        window.ApiService.cache.clear?.();
      }
      this.showToast('🧽 Cache cleared', 'success');
    } catch (error) {
      console.error('Cache clear failed:', error);
      this.showToast('Failed to clear cache', 'error');
    }
  }

  async cleanupLegacy() {
    try {
      const { cleanupLegacyStorage } = await import('../../tools/cleanupLegacyStorage.js');
      const result = await cleanupLegacyStorage();
      this.showToast(`Legacy cleanup complete. Removed: ${result.removed.join(', ') || 'none'}`, 'success');
      this.updateSystemInfo();
    } catch (error) {
      console.error('Legacy cleanup failed:', error);
      this.showToast('Legacy cleanup failed', 'error');
    }
  }

  /**
   * Handle debug mode toggle
   */
  handleDebugModeToggle(e) {
    const enabled = e.target.checked;
    
    try {
      localStorage.setItem('debug_mode', enabled.toString());
      this.settings.debugMode = enabled;
      
      if (enabled) {
        document.body.classList.add('debug-mode');
        this.showToast('Debug mode enabled - check console for logs', 'info');
        console.log('🔧 Debug mode enabled - additional logging active');
      } else {
        document.body.classList.remove('debug-mode');
        this.showToast('Debug mode disabled', 'info');
      }
      
    } catch (error) {
      console.error('Failed to toggle debug mode:', error);
      this.showToast('Failed to toggle debug mode', 'error');
    }
  }

  /**
   * Test API connection
   */
  async testApiConnection() {
    if (!this.settings.apiKey) {
      this.showToast('Please set your API key first', 'warning');
      return;
    }

    const testBtn = this.container.querySelector('#test-api-btn');
    const originalText = testBtn.innerHTML;
    
    testBtn.innerHTML = '🔄 Testing...';
    testBtn.disabled = true;

    try {
      // Test with ApiService if available
      if (window.ApiService && window.ApiService.testConnection) {
        const success = await window.ApiService.testConnection();
        
        if (success) {
          this.showToast('✅ API connection successful!', 'success');
        } else {
          this.showToast('❌ API connection failed - check your key', 'error');
        }
      } else {
        // Fallback test
        const response = await fetch('https://api.openai.com/v1/models', {
          headers: {
            'Authorization': `Bearer ${this.settings.apiKey}`
          }
        });
        
        if (response.ok) {
          this.showToast('✅ API connection successful!', 'success');
        } else {
          this.showToast('❌ API connection failed - check your key', 'error');
        }
      }
    } catch (error) {
      console.error('API test failed:', error);
      this.showToast('❌ API test failed - check your connection', 'error');
    } finally {
      testBtn.innerHTML = originalText;
      testBtn.disabled = false;
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    if (!confirm('Clear cache and temporary data?\n\nThis will remove:\n• API response cache\n• Temporary session data\n• Search history\n\nYour initiatives and settings will be preserved.')) {
      return;
    }

    try {
      // Clear specific cache keys
      const cacheKeys = [
        'api_cache', 
        'prompt_cache', 
        'temp_data', 
        'session_data',
        'search_history',
        'chat_temp',
        'analysis_cache'
      ];
      
      cacheKeys.forEach(key => localStorage.removeItem(key));
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      this.showToast('🗑️ Cache cleared successfully!', 'success');
      this.updateSystemInfo();
      
    } catch (error) {
      console.error('Cache clear failed:', error);
      this.showToast('Failed to clear cache', 'error');
    }
  }

  /**
   * Delete all initiatives only (maintenance)
   */
  async deleteInitiatives() {
    try {
      // Leverage service confirmation and clear logic
      const cleared = await InitiativeService.clearAll();
      if (cleared) {
        this.showToast('🗑️ All initiatives deleted', 'success');
        this.updateSystemInfo();
        // Inform other modules just in case
        EventBus.emit(AppEvents.INITIATIVE_DELETED, { all: true });
      }
    } catch (error) {
      console.error('Failed to delete initiatives:', error);
      this.showToast('Failed to delete initiatives', 'error');
    }
  }

  /**
   * Reset settings to defaults
   */
  resetSettings() {
    if (!confirm('Reset settings to defaults?\n\nThis will reset:\n• AI model to GPT-4 Optimized\n• Temperature to 0.7\n• Debug mode to off\n\nYour API key, initiatives, and custom prompts will be preserved.')) {
      return;
    }

    try {
      // Reset specific settings
      localStorage.setItem('ai_model', 'gpt-4o');
      localStorage.setItem('ai_temperature', '0.7');
      localStorage.setItem('debug_mode', 'false');
      
      // Remove other preference keys but keep essential data
      const preferencesToRemove = [
        'user_preferences',
        'ui_settings',
        'display_options'
      ];
      
      preferencesToRemove.forEach(key => localStorage.removeItem(key));
      
      this.showToast('🔄 Settings reset to defaults!', 'success');
      
      // Reload settings and re-render
      this.settings = this.loadSettings();
      this.render();
      this.attachEventListeners();
      
    } catch (error) {
      console.error('Settings reset failed:', error);
      this.showToast('Failed to reset settings', 'error');
    }
  }

  /**
   * Delete all data
   */
  deleteAllData() {
    const firstConfirm = confirm('⚠️ WARNING: Delete ALL data?\n\nThis will permanently delete:\n• All initiatives and objectives\n• All custom prompts\n• API key configuration\n• All strategies and analysis\n• Entire application data\n\nThis action CANNOT be undone!');
    if (!firstConfirm) return;

    const finalConfirm = confirm('🚨 FINAL WARNING 🚨\n\nAre you absolutely sure?\n\nEverything will be permanently deleted.');
    if (!finalConfirm) return;

    const confirmation = prompt('Type "DELETE" to confirm permanent data deletion:');
    if (confirmation !== 'DELETE') {
      this.showToast('Data deletion cancelled', 'info');
      return;
    }

    try {
      localStorage.clear();
      sessionStorage.clear();
      
      this.showToast('☠️ All data deleted! Reloading...', 'success');
      
      setTimeout(() => {
        location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Data deletion failed:', error);
      this.showToast('Failed to delete data', 'error');
    }
  }

  /**
   * Utility Methods
   */
  getInitiativeCount() {
    try {
      // Count both legacy and new storage locations
      const raw1 = localStorage.getItem('initiatives');
      const raw2 = localStorage.getItem('goalsDatabase');
      const pref = StorageService.get('initiatives') || null;
      const a = raw1 ? JSON.parse(raw1) : [];
      const b = raw2 ? JSON.parse(raw2) : [];
      const c = Array.isArray(pref) ? pref : [];
      const ids = new Set();
      [...a, ...b, ...c].forEach(i => i?.id && ids.add(i.id));
      return ids.size;
    } catch {
      return 0;
    }
  }

  getCustomPromptsCount() {
    try {
      const sip = StorageService.get('customPrompts');
      if (sip && typeof sip === 'object') return Object.keys(sip).length;
      const legacy = localStorage.getItem('customPrompts');
      return legacy ? Object.keys(JSON.parse(legacy)).length : 0;
    } catch {
      return 0;
    }
  }

  getStorageSize() {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    return (totalSize / 1024).toFixed(2) + ' KB';
  }

  updateSystemInfo() {
    if (!this.container) return;

    const initiativesCount = this.container.querySelector('#initiatives-count');
    const storageSize = this.container.querySelector('#storage-size');
    const apiStatus = this.container.querySelector('#api-status');
    const customPromptsCount = this.container.querySelector('#custom-prompts-count');

    if (initiativesCount) initiativesCount.textContent = this.getInitiativeCount();
    if (storageSize) storageSize.textContent = this.getStorageSize();
    if (apiStatus) apiStatus.textContent = this.settings.apiKey ? '✅ Configured' : '❌ Not Set';
    if (customPromptsCount) customPromptsCount.textContent = this.getCustomPromptsCount();
  }

  showToast(message, type = 'info') {
    if (window.showToast) {
      window.showToast(message, type);
    } else {
      console.log(`[${type.toUpperCase()}] ${message}`);
    }
  }

  activate() {
    console.log('⚙️ Settings module activated');
    
    // Reload settings from storage
    this.settings = this.loadSettings();
    
    if (this.container) {
      this.render();
      this.attachEventListeners();
      this.registerRouterActions();
    }
  }

  /**
   * Cleanup method
   */
  destroy() {
    this.removeEventListeners();
    this.container = null;
  }
}
